<template>
  <div class="container-fluid py-3 poste">
    <div v-if="chargement" class="text-center my-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2 text-muted">Ouverture du dossier…</p>
    </div>

    <div v-else-if="erreurChargement" class="alert alert-danger">{{ erreurChargement }}</div>

    <template v-else-if="d">
      <!-- En-tête -->
      <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
        <div>
          <button class="btn btn-sm btn-link px-0 text-decoration-none" @click="$router.back()"><i class="bi bi-arrow-left"></i> Retour</button>
          <h4 class="fw-bold mb-0 text-primary">
            <i class="bi bi-clipboard2-pulse me-1"></i> Consultation {{ c.numero || '' }}
            <span class="badge ms-2 fs-6" :class="badgeStatut">{{ c.statut }}</span>
          </h4>
          <div class="small text-muted">
            {{ c.type?.nom }}<span v-if="c.medecin"> · {{ c.medecin.nom }}</span>
            <span v-if="c.priseEnChargeLe"> · débutée à {{ heure(c.priseEnChargeLe) }}</span>
            <span v-if="c.clotureeLe"> · clôturée le {{ dateHeure(c.clotureeLe) }}</span>
          </div>
        </div>
        <div class="d-flex flex-wrap gap-2">
          <button v-if="d.droits.reprendre && !d.droits.modifier" class="btn btn-outline-warning" @click="reprendre"><i class="bi bi-arrow-repeat me-1"></i>Reprendre le patient</button>
          <button v-if="d.droits.rouvrir?.possible" class="btn btn-outline-warning" @click="rouvrir"><i class="bi bi-unlock me-1"></i>Rouvrir la consultation</button>
          <div class="dropdown">
            <button v-dropdown-fixed type="button" class="btn btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false"><i class="bi bi-printer me-1"></i>Imprimer</button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li><button class="dropdown-item" :disabled="!d.ordonnance?.lignes?.length" @click="imprimerDoc('ordonnance')"><i class="bi bi-capsule me-2"></i>Ordonnance</button></li>
              <li><button class="dropdown-item" :disabled="!examensActifs" @click="imprimerDoc('examens')"><i class="bi bi-file-medical me-2"></i>Demande d'examens</button></li>
              <li><button class="dropdown-item" @click="imprimerDoc('compteRendu')"><i class="bi bi-file-earmark-text me-2"></i>Compte rendu</button></li>
              <li><hr class="dropdown-divider"></li>
              <li><button class="dropdown-item" @click="imprimerDoc('vierge-ordonnance')">Ordonnance vierge</button></li>
              <li><button class="dropdown-item" @click="imprimerDoc('vierge-examens')">Demande d'examens vierge</button></li>
            </ul>
          </div>
        </div>
      </div>

      <!-- Situations particulières -->
      <div v-if="c.annulee" class="alert alert-danger"><i class="bi bi-x-octagon me-1"></i>Consultation annulée<span v-if="c.motifAnnulation"> : {{ c.motifAnnulation }}</span>.</div>
      <div v-else-if="c.cloturee" class="alert alert-success py-2">
        <i class="bi bi-lock me-1"></i>Consultation clôturée : le dossier est figé. Vous pouvez y ajouter un complément en bas de page.
        <div v-if="d.droits.rouvrir?.possible && d.droits.rouvrir.jusquA" class="small mt-1">Clôturée par erreur ? Vous pouvez la rouvrir jusqu'au {{ dateHeure(d.droits.rouvrir.jusquA) }}.</div>
        <div v-else-if="!d.droits.rouvrir?.possible && d.droits.rouvrir?.raison && $store.getters.hasPermission('CONSULT_TRAITER')" class="small mt-1 text-muted">Réouverture impossible : {{ d.droits.rouvrir.raison }}</div>
      </div>
      <div v-if="!c.annulee && !c.cloturee && c.reouverture" class="alert alert-warning py-2">
        <i class="bi bi-unlock me-1"></i>Consultation rouverte le {{ dateHeure(c.reouverture.date) }}<span v-if="c.reouverture.par"> par {{ c.reouverture.par.nom }}</span> : {{ c.reouverture.motif }}. Terminez-la puis clôturez-la à nouveau.
      </div>
      <div v-else-if="paiementRequis" class="alert alert-warning py-2 d-flex flex-wrap align-items-center gap-2">
        <span class="me-auto"><i class="bi bi-cash-coin me-1"></i>Consultation pas encore payée : le patient doit d'abord passer à la caisse.</span>
        <button v-if="d.droits.sansPaiement && !c.cloturee && !c.annulee" class="btn btn-sm btn-outline-danger" :disabled="!!envoi" @click="urgence">
          <i class="bi bi-exclamation-triangle me-1"></i>Urgence : voir le patient avant le paiement
        </button>
      </div>
      <div v-if="c.priseSansPaiement" class="alert alert-light border border-danger-subtle py-2 small">
        <i class="bi bi-exclamation-triangle text-danger me-1"></i>Pris en charge avant paiement (urgence) le {{ dateHeure(c.priseSansPaiement.date) }} par {{ c.priseSansPaiement.par }} : {{ c.priseSansPaiement.motif }}<span v-if="!c.reglee"> — consultation à encaisser.</span>
      </div>
      <div v-else-if="d.droits.reprendre && !d.droits.modifier" class="alert alert-info py-2"><i class="bi bi-person-badge me-1"></i>Ce patient est suivi par {{ c.medecin?.nom }}. Pour écrire dans son dossier, reprenez-le.</div>

      <bandeau-patient :patient="d.patient || {}" :accompagnant="d.accompagnant || null" v-model:allergies="f.allergies" v-model:antecedents="f.antecedents" :modifiable="modifiable" />

      <div class="row g-3">
        <div class="col-lg-8">
          <!-- 1. Anamnèse -->
          <section class="card border-0 shadow-sm rounded-4 mb-3">
            <div class="card-body">
              <h6 class="etape"><span class="num">1</span>Motif et histoire de la maladie</h6>
              <label class="form-label small fw-semibold">Motif de consultation <span class="text-danger">*</span></label>
              <input v-model="f.motif" class="form-control mb-2" :disabled="!modifiable" placeholder="Ex. fièvre et céphalées depuis 3 jours">
              <label class="form-label small fw-semibold">Histoire de la maladie</label>
              <textarea v-model="f.histoireMaladie" class="form-control" rows="3" :disabled="!modifiable" placeholder="Début, évolution, signes associés, traitements déjà pris…"></textarea>
            </div>
          </section>

          <!-- 2. Examen clinique -->
          <section class="card border-0 shadow-sm rounded-4 mb-3">
            <div class="card-body">
              <h6 class="etape"><span class="num">2</span>Examen clinique</h6>
              <textarea v-model="f.examenClinique" class="form-control" rows="4" :disabled="!modifiable" placeholder="État général, examen par appareil, signes positifs et négatifs pertinents…"></textarea>
              <small class="text-muted">Les constantes du jour sont dans le volet de droite (bouton « Prendre » pour les saisir).</small>
            </div>
          </section>

          <!-- 3. Diagnostic -->
          <section class="card border-0 shadow-sm rounded-4 mb-3">
            <div class="card-body">
              <h6 class="etape"><span class="num">3</span>Diagnostic <span class="text-danger">*</span></h6>
              <diagnostics-cim10 v-model="f.diagnostics" :lecture-seule="!modifiable" />
            </div>
          </section>

          <!-- 4. Examens complémentaires -->
          <section class="card border-0 shadow-sm rounded-4 mb-3">
            <div class="card-body">
              <h6 class="etape"><span class="num">4</span>Examens complémentaires</h6>
              <prescription-examens v-model="f.examens" :services="services" :labo="d.modules.laboratoire" :lecture-seule="!modifiable" />
            </div>
          </section>

          <!-- 5. Ordonnance -->
          <section class="card border-0 shadow-sm rounded-4 mb-3">
            <div class="card-body">
              <h6 class="etape"><span class="num">5</span>Ordonnance <small v-if="d.ordonnance?.numero" class="text-muted fw-normal ms-2">{{ d.ordonnance.numero }}</small></h6>
              <prescription-ordonnance v-model="f.ordonnance" :frequences="frequences" :durees="durees" :conditionnements="conditionnements"
                                       :allergies="f.allergies" :pharmacie="d.modules.pharmacie" :lecture-seule="!modifiable" />
            </div>
          </section>

          <!-- 6. Conclusion -->
          <section class="card border-0 shadow-sm rounded-4 mb-3">
            <div class="card-body">
              <h6 class="etape"><span class="num">6</span>Conduite à tenir et suite</h6>
              <label class="form-label small fw-semibold">Conduite à tenir, conseils au patient</label>
              <textarea v-model="f.conduiteATenir" class="form-control mb-3" rows="3" :disabled="!modifiable" placeholder="Repos, hydratation, signes d'alerte pour revenir…"></textarea>
              <div class="row g-2">
                <div class="col-md-5">
                  <label class="form-label small fw-semibold">Orientation</label>
                  <select v-model="f.orientation" class="form-select" :disabled="!modifiable">
                    <option :value="null">—</option>
                    <option v-for="(lib, code) in d.orientations" :key="code" :value="code">{{ lib }}</option>
                  </select>
                </div>
                <div class="col-md-4">
                  <label class="form-label small fw-semibold">Contrôle le <span v-if="f.orientation === 'CONTROLE'" class="text-danger">*</span></label>
                  <input v-model="f.prochainRdv" type="date" class="form-control" :min="aujourdhui" :disabled="!modifiable">
                </div>
                <div v-if="d.modules.rendezVous && f.prochainRdv && !c.rendezVousSuivi" class="col-md-3">
                  <label class="form-label small fw-semibold">Heure</label>
                  <input v-model="heureRdv" type="time" class="form-control" :disabled="!modifiable">
                </div>
              </div>
              <div v-if="c.rendezVousSuivi" class="small text-success mt-2"><i class="bi bi-calendar-check me-1"></i>Rendez-vous de contrôle inscrit à l'agenda : {{ dateHeure(c.rendezVousSuivi.debut) }}</div>
              <div v-else-if="d.modules.rendezVous && f.prochainRdv && modifiable" class="small text-muted mt-1">Le rendez-vous sera inscrit dans votre agenda à la clôture.</div>

              <div class="d-flex flex-wrap gap-2 mt-3">
                <router-link v-if="d.modules.hospitalisation && f.orientation === 'HOSPITALISATION' && $store.getters.hasPermission('HOSP_CREATE')"
                             class="btn btn-sm btn-outline-danger" :to="{ name: 'create_hospitalisation', query: { patient: d.patient?.id } }">
                  <i class="bi bi-hospital me-1"></i>Hospitaliser le patient
                </router-link>
                <router-link v-if="d.modules.documents && $store.getters.hasPermission('DOCUMENTS_REDIGER')"
                             class="btn btn-sm btn-outline-secondary" :to="{ name: 'documents_patient', params: { id: d.patient?.id }, query: { consultation: c.id } }">
                  <i class="bi bi-file-earmark-medical me-1"></i>Certificat, arrêt de travail, lettre
                </router-link>
              </div>

              <label class="form-label small fw-semibold mt-3">Notes internes <small class="text-muted fw-normal">(non imprimées)</small></label>
              <textarea v-model="f.observations" class="form-control" rows="2" :disabled="!modifiable"></textarea>
            </div>
          </section>

          <!-- Compléments après clôture -->
          <section v-if="c.cloturee || d.complements.length" class="card border-0 shadow-sm rounded-4 mb-3">
            <div class="card-body">
              <h6 class="fw-bold"><i class="bi bi-journal-plus me-1"></i>Compléments au dossier</h6>
              <div v-for="k in d.complements" :key="k.id" class="complement mb-2">
                <div class="small text-muted">{{ dateHeure(k.date) }} — {{ k.auteur?.nom }}</div>
                <div class="texte">{{ k.texte }}</div>
              </div>
              <div v-if="d.droits.completer && $store.getters.hasPermission('CONSULT_TRAITER')" class="mt-2">
                <textarea v-model="complement" class="form-control" rows="2" placeholder="Résultat reçu, appel du patient, précision…"></textarea>
                <div class="text-end mt-2">
                  <button class="btn btn-sm btn-primary" :disabled="!complement.trim() || !!envoi" @click="ajouterComplement">Ajouter au dossier</button>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- Volet de droite -->
        <div class="col-lg-4">
          <div class="volet">
            <resume-constantes :patient-id="d.patient?.id" :consultation-id="c.id" />
            <historique-patient :historique="d.historique" :resultats="d.resultatsRecents" />
          </div>
        </div>
      </div>

      <!-- Barre d'actions -->
      <div v-if="modifiable" class="barre-actions shadow">
        <div class="small me-auto" :class="sale ? 'text-warning' : 'text-muted'">
          <i :class="sale ? 'bi bi-circle-fill' : 'bi bi-check2-circle'"></i>
          {{ sale ? 'Modifications non enregistrées' : (enregistreA ? 'Enregistré à ' + enregistreA : 'À jour') }}
        </div>
        <button v-if="c.statut === 'En attente'" class="btn btn-outline-primary" :disabled="!!envoi" @click="commencer"><i class="bi bi-megaphone me-1"></i>Appeler le patient</button>
        <button class="btn btn-outline-success" :disabled="!!envoi || !sale" @click="enregistrer()">
          <span v-if="envoi === 'enregistrer'" class="spinner-border spinner-border-sm me-1"></span><i v-else class="bi bi-save me-1"></i>Enregistrer
        </button>
        <button class="btn btn-success" :disabled="!!envoi" @click="cloturer">
          <span v-if="envoi === 'cloturer'" class="spinner-border spinner-border-sm me-1"></span><i v-else class="bi bi-check2-all me-1"></i>Clôturer la consultation
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import api, { messageErreur, notifier } from '@/api'
import { imprimer } from '@/impression/impression'
import { documentOrdonnance, documentDemandeExamens, documentCompteRendu, documentFicheVierge } from '@/impression/documents/consultation'
import { confirmer, demanderMotif } from '@/components/commun/dialogue'
import ResumeConstantes from '@/components/accueil/ResumeConstantes.vue'
import BandeauPatient from '@/components/consultation/BandeauPatient.vue'
import DiagnosticsCim10 from '@/components/consultation/DiagnosticsCim10.vue'
import PrescriptionExamens, { ligneExamen } from '@/components/consultation/PrescriptionExamens.vue'
import PrescriptionOrdonnance, { ligneOrdonnance, motsAllergies, allergieDe } from '@/components/consultation/PrescriptionOrdonnance.vue'
import HistoriquePatient from '@/components/consultation/HistoriquePatient.vue'

const AUTO_ENREGISTREMENT_MS = 60000

export default {
  name: 'TraitementConsultation',
  components: { ResumeConstantes, BandeauPatient, DiagnosticsCim10, PrescriptionExamens, PrescriptionOrdonnance, HistoriquePatient },
  data() {
    return {
      d: null,
      f: null,
      chargement: true,
      erreurChargement: '',
      envoi: '',
      sale: false,
      enregistreA: '',
      heureRdv: '08:00',
      complement: '',
      services: [],
      frequences: [],
      durees: [],
      conditionnements: [],
      minuteur: null,
      aujourdhui: dayjs().format('YYYY-MM-DD'),
    }
  },
  computed: {
    c() { return this.d?.consultation || {} },
    modifiable() { return !!this.d?.droits?.modifier && !this.paiementRequis },
    // Le serveur décide (réglage de la clinique, gratuité, urgence déjà déclarée)
    paiementRequis() { return !!this.d && !!this.c.paiementBloquant && !this.c.cloturee && !this.c.annulee },
    examensActifs() { return (this.d?.examens || []).some(x => x.statut !== 'ANNULE') },
    badgeStatut() {
      const s = String(this.c.statut || '').toLowerCase()
      if (s.startsWith('termin')) return 'bg-success'
      if (s.startsWith('annul')) return 'bg-danger'
      if (s === 'en cours') return 'bg-primary'
      return 'bg-warning text-dark'
    },
  },
  watch: {
    f: {
      deep: true,
      handler(_, ancien) {
        if (ancien) this.sale = true
      },
    },
  },
  async mounted() {
    window.addEventListener('beforeunload', this.avantFermeture)
    this.minuteur = setInterval(() => { if (this.sale && this.modifiable && !this.envoi) this.enregistrer({ silencieux: true }) }, AUTO_ENREGISTREMENT_MS)
    await Promise.all([this.charger(), this.chargerReferentiels()])
  },
  beforeUnmount() {
    window.removeEventListener('beforeunload', this.avantFermeture)
    clearInterval(this.minuteur)
  },
  async beforeRouteLeave() {
    if (!this.sale || !this.modifiable) return true
    return confirmer({ titre: 'Quitter la consultation', message: 'Des modifications ne sont pas enregistrées. Quitter quand même ?', libelleValider: 'Quitter', danger: true })
  },
  methods: {
    avantFermeture(ev) {
      if (this.sale && this.modifiable) {
        ev.preventDefault()
        ev.returnValue = ''
      }
    },
    remplir(d) {
      this.d = d
      const c = d.consultation
      this.f = {
        motif: c.motif || '',
        histoireMaladie: c.histoireMaladie || '',
        examenClinique: c.examenClinique || '',
        diagnostics: (c.diagnostics || []).map(x => ({ ...x })),
        conduiteATenir: c.conduiteATenir || '',
        orientation: c.orientation || null,
        prochainRdv: c.prochainRdv || '',
        observations: c.observations || '',
        allergies: d.patient?.allergies || '',
        antecedents: d.patient?.antecedents || '',
        examens: (d.examens || []).map(ligneExamen),
        ordonnance: (d.ordonnance?.lignes || []).map(ligneOrdonnance),
      }
      this.$nextTick(() => { this.sale = false })
    },
    /** Les nouveaux résultats de cette consultation sont maintenant vus par le médecin (badge gardé à l'écran). */
    resultatsVus(d) {
      const ids = (d.examens || []).filter(x => x.statut === 'VALIDE' && x.vu === false).map(x => x.id)
      if (ids.length && this.$store.getters.hasPermission('CONSULT_TRAITER')) {
        api.post('mes-resultats/vus', { ids }).catch(() => {})
      }
    },
    async charger() {
      this.chargement = !this.d
      try {
        const res = await api.getItem(`consultations/${this.$route.params.id}`, 'dossier')
        this.remplir(res.data)
        this.resultatsVus(res.data)
      } catch (e) {
        this.erreurChargement = messageErreur(e, "Impossible d'ouvrir ce dossier.")
      } finally {
        this.chargement = false
      }
    },
    async chargerReferentiels() {
      const [s, fr, du, co] = await Promise.allSettled([
        api.getItems('services'), api.getItems('frequences'), api.getItems('durees'), api.getItems('conditionnements'),
      ])
      const lignes = r => (r.status === 'fulfilled' && Array.isArray(r.value.data?.data) ? r.value.data.data : [])
      // Tarif affiché au moment de prescrire (ce que le patient paiera à la caisse)
      this.services = lignes(s).filter(x => x.isEnabled !== false).map(x => ({ id: Number(x.id), nom: x.nom, prix: x.prix, label: [x.type?.nom, x.nom].filter(Boolean).join(' — ') + (x.prix ? ` · ${Number(x.prix).toLocaleString('fr-FR')} F` : '') }))
      this.frequences = lignes(fr)
      this.durees = lignes(du)
      this.conditionnements = lignes(co)
    },
    charge() {
      const f = this.f
      return {
        motif: f.motif,
        histoireMaladie: f.histoireMaladie,
        examenClinique: f.examenClinique,
        diagnostics: f.diagnostics,
        conduiteATenir: f.conduiteATenir,
        orientation: f.orientation,
        prochainRdv: f.prochainRdv || null,
        observations: f.observations,
        allergies: f.allergies,
        antecedents: f.antecedents,
        examens: f.examens.map(x => ({ id: x.id, serviceId: x.serviceId, type: x.type, precision: x.precision, urgent: x.urgent, resultat: x.resultat })),
        ordonnance: f.ordonnance.map(l => ({
          id: l.id, produitId: l.produitId, medicament: l.medicament, quantite: l.quantite === '' ? null : l.quantite,
          posologie: l.posologie, instructions: l.instructions,
          frequence_id: l.frequenceId, duree_id: l.dureeId, conditionnement_id: l.conditionnementId,
          motifAllergie: allergieDe(l.medicament, motsAllergies(f.allergies)) ? (l.motifAllergie || null) : null,
        })),
      }
    },
    /** Lignes de l'ordonnance qui touchent une allergie connue du patient. */
    alertesAllergie() {
      const mots = motsAllergies(this.f.allergies)
      return this.f.ordonnance.filter(l => allergieDe(l.medicament, mots)).map(l => l.medicament)
    },
    async enregistrer({ silencieux = false } = {}) {
      if (!this.f.motif.trim()) {
        if (!silencieux) notifier('Indiquez le motif de consultation.', 'warning')
        return false
      }
      this.envoi = 'enregistrer'
      try {
        const res = await api.updateItem(`consultations/${this.c.id}`, 'traitements', this.charge())
        this.remplir(res.data)
        this.enregistreA = dayjs().format('HH:mm')
        if (!silencieux) notifier('Dossier enregistré.', 'success')
        return true
      } catch (e) {
        if (e?.response?.status !== 403 || !silencieux) notifier(messageErreur(e, "L'enregistrement a échoué."))
        return false
      } finally {
        this.envoi = ''
      }
    },
    /** Salle du médecin (choisie dans la salle d'attente), annoncée sur l'écran. */
    salleAppel() {
      try {
        const salle = localStorage.getItem('keneya.salleAppel')
        return salle ? { salle } : {}
      } catch (e) {
        return {}
      }
    },
    async commencer() {
      this.envoi = 'commencer'
      try {
        this.remplir((await api.post(`consultations/${this.c.id}/prise-en-charge`, this.salleAppel())).data)
      } catch (e) {
        notifier(messageErreur(e))
      } finally {
        this.envoi = ''
      }
    },
    async urgence() {
      const r = await demanderMotif({
        titre: 'Urgence : voir le patient avant le paiement',
        message: "Le motif, votre nom et l'heure restent au dossier. La caisse encaissera la consultation ensuite.",
        libelle: "Motif de l'urgence",
        placeholder: 'Ex. : détresse respiratoire, douleur thoracique, enfant fébrile',
        libelleValider: 'Prendre en charge',
      })
      if (!r?.motif) return
      this.envoi = 'commencer'
      try {
        this.remplir((await api.post(`consultations/${this.c.id}/prise-en-charge`, { motifUrgence: r.motif, ...this.salleAppel() })).data)
        notifier('Urgence inscrite au dossier : vous pouvez soigner le patient.', 'success')
      } catch (e) {
        notifier(messageErreur(e))
      } finally {
        this.envoi = ''
      }
    },
    async reprendre() {
      const ok = await confirmer({ titre: 'Reprendre le patient', message: `Ce patient est suivi par ${this.c.medecin?.nom}. Le reprendre et devenir son médecin pour cette consultation ?`, libelleValider: 'Reprendre' })
      if (!ok) return
      try {
        this.remplir((await api.post(`consultations/${this.c.id}/prise-en-charge`, { reprendre: true })).data)
        notifier('Vous suivez maintenant ce patient.', 'success')
      } catch (e) {
        notifier(messageErreur(e))
      }
    },
    async rouvrir() {
      const reponse = await demanderMotif({
        titre: 'Rouvrir la consultation',
        message: "La consultation repassera « En cours » pour que vous la terminiez. Le contrôle programmé à la clôture sera annulé puis reprogrammé à la nouvelle clôture. La réouverture est inscrite au dossier.",
        libelle: 'Motif de la réouverture',
        placeholder: 'Ex. : clôturée par erreur, examen clinique incomplet',
        libelleValider: 'Rouvrir',
        danger: false,
      })
      if (!reponse?.motif) return
      try {
        this.remplir((await api.post(`consultations/${this.c.id}/reouverture`, { motif: reponse.motif })).data)
        notifier('Consultation rouverte : vous pouvez la compléter.', 'success')
      } catch (e) {
        notifier(messageErreur(e))
      }
    },
    async cloturer() {
      const manque = []
      if (!this.f.motif.trim()) manque.push('le motif')
      if (!this.f.diagnostics.length) manque.push('au moins un diagnostic')
      if (this.f.orientation === 'CONTROLE' && !this.f.prochainRdv) manque.push('la date du contrôle')
      if (manque.length) {
        notifier('Pour clôturer, indiquez ' + manque.join(', ') + '.', 'warning')
        return
      }
      const allergies = this.alertesAllergie()
      const aConfirmer = this.f.ordonnance.filter(l => allergieDe(l.medicament, motsAllergies(this.f.allergies)) && !l.motifAllergie)
      if (aConfirmer.length) {
        notifier(`Allergie connue du patient : retirez ${aConfirmer.map(l => '« ' + l.medicament + ' »').join(', ')} de l'ordonnance, ou cliquez « Maintenir » et indiquez le motif.`, 'warning')
        return
      }
      const message = [
        allergies.length ? `⚠ Prescription maintenue malgré l'allergie : ${allergies.join(', ')}.` : '',
        'Après la clôture, le dossier est figé (vous pourrez encore y ajouter des compléments).',
        this.f.prochainRdv && this.d.modules.rendezVous ? `Le contrôle du ${dayjs(this.f.prochainRdv).format('DD/MM/YYYY')} à ${this.heureRdv} sera inscrit dans l'agenda.` : '',
      ].filter(Boolean).join('\n')
      const ok = await confirmer({ titre: 'Clôturer la consultation', message, libelleValider: 'Clôturer', danger: allergies.length > 0 })
      if (!ok) return
      this.envoi = 'cloturer'
      try {
        const res = await api.post(`consultations/${this.c.id}/cloture`, { ...this.charge(), heureRdv: this.heureRdv })
        this.remplir(res.data)
        notifier('Consultation clôturée.', 'success')
      } catch (e) {
        notifier(messageErreur(e, 'La clôture a échoué.'))
      } finally {
        this.envoi = ''
      }
    },
    async ajouterComplement() {
      this.envoi = 'complement'
      try {
        this.remplir((await api.post(`consultations/${this.c.id}/complements`, { texte: this.complement })).data)
        this.complement = ''
      } catch (e) {
        notifier(messageErreur(e))
      } finally {
        this.envoi = ''
      }
    },
    async imprimerDoc(type) {
      if (type === 'ordonnance' && this.f) {
        const aConfirmer = this.f.ordonnance.filter(l => allergieDe(l.medicament, motsAllergies(this.f.allergies)) && !l.motifAllergie)
        if (aConfirmer.length) {
          notifier(`Allergie connue du patient : retirez ou maintenez (avec motif) ${aConfirmer.map(l => '« ' + l.medicament + ' »').join(', ')} avant d'imprimer l'ordonnance.`, 'warning')
          return
        }
      }
      if (this.sale && this.modifiable && !type.startsWith('vierge')) {
        if (!(await this.enregistrer({ silencieux: true }))) return
      }
      const docs = {
        ordonnance: () => documentOrdonnance(this.d),
        examens: () => documentDemandeExamens(this.d),
        compteRendu: () => documentCompteRendu(this.d),
        'vierge-ordonnance': () => documentFicheVierge(this.d, 'ordonnance'),
        'vierge-examens': () => documentFicheVierge(this.d, 'examens'),
      }
      await imprimer(docs[type]())
    },
    heure(d) { return d ? dayjs(d).format('HH:mm') : '' },
    dateHeure(d) { return d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '' },
  },
}
</script>

<style scoped>
.poste { padding-bottom: 12px; }
.etape { font-weight: 700; color: #0d3c78; margin-bottom: 12px; display: flex; align-items: center; gap: 8px; }
.etape > .num { width: 24px; height: 24px; border-radius: 50%; background: #0d6efd; color: #fff; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; }
.volet { position: sticky; top: 76px; }
.barre-actions { position: sticky; bottom: 0; z-index: 1020; margin: 0 -12px; border-radius: 12px 12px 0 0; background: #fff; border-top: 1px solid #e3e8ef; padding: 10px 24px; display: flex; gap: 8px; align-items: center; justify-content: flex-end; flex-wrap: wrap; }
.complement { border-left: 3px solid #0d6efd; padding-left: 8px; }
.texte { white-space: pre-line; }
textarea:disabled, input:disabled, select:disabled { background: #fbfcfe; color: #212529; }
</style>
